import { FC } from 'react';
import { BlueprintEntity } from '@agilelab/plugin-wb-builder-common';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import { Severity, SeverityTooltip } from '@agilelab/plugin-wb-platform';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { Button } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { catalogLocationCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { Box, Theme, createStyles, makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router';
import { selectedBlueprintRouteRef } from '../../../routes';
import { BlueprintCardActions } from './BlueprintCardActions';

const useStyles = (error: boolean = false) =>
  makeStyles((theme: Theme) =>
    createStyles({
      footer: {
        background: error ? theme.palette.bkg.error : theme.palette.bkg.primary,
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      chooseButton: {
        '&:hover': {
          textDecoration: 'none',
        },
        ...(error
          ? {
              color: theme.palette.softened.error,
              borderColor: theme.palette.softened.error,
            }
          : {}),
      },
    }),
  );

interface Props {
  blueprint: BlueprintEntity;
  error?: EntityErrorResult;
}

export const BlueprintCardFooter: FC<Props> = ({ blueprint, error }) => {
  const hasError = Boolean(error?.errorMessage);
  const classes = useStyles(hasError)();
  const location = useLocation();
  const blueprintRoute = useRouteRef(selectedBlueprintRouteRef);
  const { allowed } = usePermission({
    permission: catalogLocationCreatePermission,
  });

  const { name } = parseEntityRef(stringifyEntityRef(blueprint));
  const href = blueprintRoute({ blueprintName: name });
  const knownDataproduct = location.state?.dataproduct;
  const knownDomain = location.state?.domain;

  return (
    <Box className={classes.footer}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <BlueprintCardActions blueprint={blueprint} />

        {error?.errorMessage && (
          <SeverityTooltip
            severity={Severity.Error}
            label={error.errorMessage}
          />
        )}
      </Box>

      {allowed && (
        <Button
          variant="outlined"
          color={hasError ? undefined : 'primary'}
          to={
            knownDomain && knownDataproduct
              ? `${href}?formData=%7B"domain"%3A"${knownDomain}"%2C"dataproduct"%3A"system:${knownDataproduct}"%7D`
              : href
          }
          disabled={!!error?.errorMessage}
          className={classes.chooseButton}
          size="small"
        >
          Choose
        </Button>
      )}
    </Box>
  );
};
